import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { isBedbankItem, isLEHotelItem, isExperienceItem, isTransferItem, isLuxPlusSubscriptionItem, isInstantBookingLEHotelItem, isInsuranceItem, isFlightItem, isMerchandiseItem } from 'lib/checkout/checkoutUtils'
import { useSelectCartOffers, useShouldWaiveLuxPlusJoiningFee } from './useCartItems'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { getItemUniqueKey } from 'checkout/lib/utils/accommodation/cart'
import { getBedbankItemViewTotals } from 'checkout/lib/utils/bedbank/view'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { getMerchandiseVariantOptionText } from 'components/Merchandise/merchandiseUtils'
import { useContext, useMemo } from 'react'
import { isBedbankOffer, isExperienceOffer, isLEHotel } from 'lib/offer/offerTypes'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import { useExperienceTimesForCart } from './useExperienceTimesForCart'
import { formatDynamicDateRange, formatExperienceDateRange } from 'lib/datetime/dateUtils'
import moment from 'moment'
import { ACCOMMODATION_TIME_FORMAT } from 'constants/dateFormats'
import { getFlightLoyaltyProductType, getFlightLoyaltyProductTypeRank, getFlightsTotal, TITLE_MAPPER } from 'lib/flights/flightUtils'
import { FIXED_SECTION_NAMES, getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { last, sortBy, sum, isNonNullable, arrayToMap } from 'lib/array/arrayUtils'
import { FlightViewTypes } from 'constants/flight'
import { CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE } from 'constants/checkout'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { matchBedbankRateOrItem } from 'lib/hotels/bedbankUtils'
import { generateLoyaltyPointsCalculatorBedbankOptions, generateLoyaltyPointsCalculatorExperienceOptions, generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorMerchandiseOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import getLEPackageItemTotals from 'checkout/lib/utils/accommodation/getLEPackageItemTotals'
import useMerchandiseOffers from 'hooks/Merchandise/useMerchandiseOffers'
import getLoyaltyEarnKeyFromFlightItems from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKeyFromFlightItems'
import { getCartMachineActor } from 'machines/cart/MultiCartMachineProvider'
import { selectItemsInActiveCart } from 'machines/cart/selectors'
import { useSelector } from '@xstate/react'

export interface CartItemData {
  fetching: boolean
  error: boolean
  itemId: string
  itemType: App.Checkout.AnyItemType
  itemTotals?: App.Checkout.ItemViewTotals | undefined
  isDeleted: boolean
  isSavedForLater: boolean
  luxPlus?: {
    hasMemberInclusions: boolean
    hasMemberPrices: boolean
    hasMembershipRequired: boolean
  }
  subtitle?: string
  duration?: number
  groupHeading?: string
  roomImage?: App.Image
  maxPassengers?: number
  maxLuggage?: number
  tickets?: Array<{
    name: string
    count: number
    price: number
  }>
  occupancy?: App.Occupants
  sectionHeading?: string
  sectionId: string
  offerId?: string
  startDate?: string
  endDate?: string
  pointsEarnCalculationRequest?: App.LoyaltyPointsEarnCalculationRequest | undefined
  locationHeading?: string
  paymentMethod?: App.BedbankPaymentMethod
  bundledItemIds?: Array<string>
}

export interface CartItemMetadata {
  fetching: boolean
  error: boolean
  items: Array<CartItemData>
}

const defaultItemState = {
  saveForLater: false,
  deleted: false,
} as const

const itemOrderMap: Map<App.Checkout.AnyItemType, number> = new Map([
  ['luxury-plus-subscription', 0],
  ['subscription-join', 1],
  ['insurance', 2],
  ['hotel', 3],
  ['bedbankHotel', 4],
  ['experience', 5],
  ['transfer', 6],
  ['merchandise', 7],
])

function useAllUserCartItemsMetadata(items: Array<App.Checkout.AnyItem>): CartItemMetadata {
  const cartOffers = useSelectCartOffers()
  const waiveLuxPlusJoiningFee = useShouldWaiveLuxPlusJoiningFee()
  const {
    offerErrors,
    offersLoading,
    experiencesError,
    experiencesLoading,
    calendarsByOccupancy,
    calendarsByOccupancyLoading,
    availableRatesByOccupancy,
    bedbankOfferRates,
    bedbankOfferRatesLoading,
    subscriptionOffers,
    subscriptionOffersLoading,
    subscriptionOffersError,
    luxPlusLPPEnabled,
    merchandiseFetching,
    merchandiseErrors,
  } = useAppSelector((state) => ({
    offerErrors: state.offer.offerErrors,
    offersLoading: state.offer.offersLoading,
    experiencesError: state.experience.experienceErrors,
    experiencesLoading: state.experience.fetchingExperiences,
    calendarsByOccupancy: state.calendar.calendarsByOccupancy,
    calendarsByOccupancyLoading: state.calendar.calendarsByOccupancyLoading,
    availableRatesByOccupancy: state.offer.offerAvailableRatesByOccupancy,
    bedbankOfferRates: state.offer.bedbankOfferRates,
    bedbankOfferRatesLoading: state.offer.bedbankOfferRatesLoading,
    subscriptionOffers: state.luxPlus.products.subscription.offers,
    subscriptionOffersLoading: state.luxPlus.products.subscription.fetching,
    subscriptionOffersError: state.luxPlus.products.subscription.error,
    luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(state),
    merchandiseFetching: state.merchandise.fetchingMerchandise,
    merchandiseErrors: state.merchandise.merchandiseErrors,
  }), shallowEqual)

  const multiCartActorRef = getCartMachineActor() ?? undefined
  const selectedItemStatesMap = useSelector(multiCartActorRef, (snapshot) => {
    if (!snapshot) return new Map<string, { deleted: boolean, saveForLater: boolean }>()
    const allItems = selectItemsInActiveCart(snapshot)
    const map = new Map<string, { deleted: boolean, saveForLater: boolean }>()
    allItems.forEach(multiCartItem => {
      map.set(multiCartItem.item.itemId, {
        deleted: multiCartItem.state === 'deleted',
        saveForLater: multiCartItem.state === 'save_for_later',
      })
    })
    return map
  })

  const itemStatesMap = useMemo(() => {
    return selectedItemStatesMap ?? new Map<string, { deleted: boolean, saveForLater: boolean }>()
  }, [selectedItemStatesMap])

  const activeItemIds = useMemo(() => {
    return Array.from(itemStatesMap.entries())
      .filter(([_, state]) => !state.deleted && !state.saveForLater)
      .map(([itemId]) => itemId)
  }, [itemStatesMap])

  const itemsSet = useMemo(() => new Set(activeItemIds), [activeItemIds])

  const bedbankItems = useMemo(() => items.filter(isBedbankItem), [items])
  const experienceItems = useMemo(() => items.filter(isExperienceItem), [items])
  const flightItems = useMemo(() => items.filter(isFlightItem), [items])
  const merchandiseItems = useMemo(() => items.filter(isMerchandiseItem), [items])
  const { airports, currentRegionCode } = useContext(GeoContext)

  const experienceTimesMap = useExperienceTimesForCart(experienceItems)
  const [merchandiseOffers] = useMerchandiseOffers(merchandiseItems.map(item => item.advertId))

  const metadataItems = useMemo(() => {
    const hotelItems = items.filter(isLEHotelItem)
    const transferItems = sortBy(items.filter(isTransferItem), item => new Date(item.transfer.date ?? 0), 'asc')
    const subscriptionItem = items.find(isLuxPlusSubscriptionItem)
    const insuranceItems = items.filter(isInsuranceItem)
    const result: Array<CartItemData> = []

    hotelItems.forEach((item) => {
      const offer = cartOffers[item.offerId]
      const offerError = offerErrors[item.offerId]
      const offerLoading = offersLoading[item.offerId]
      const itemState = itemStatesMap.get(item.itemId) ?? defaultItemState
      let itemTotals: App.Checkout.ItemViewTotals | undefined
      let ratesLoading = !offer
      let pkg

      if (offer && isLEHotel(offer)) {
        const uniqueKey = getItemUniqueKey(item)
        pkg = offer.packages.find(p => p.uniqueKey === uniqueKey)

        if (pkg && isInstantBookingLEHotelItem(item)) {
          const occupancyKey = generateOccupancyStringByRoom(item.occupancy)
          ratesLoading = ratesLoading || calendarsByOccupancyLoading[occupancyKey]?.[item.offerId]

          if (offer.type === OFFER_TYPE_ALWAYS_ON && item.checkIn && item.checkOut) {
            const key = buildAvailableRateKey(item.checkIn, item.checkOut, [item.occupancy])
            ratesLoading = ratesLoading || availableRatesByOccupancy[offer.id]?.[key]?.fetching
          }

          itemTotals = getLEPackageItemTotals(
            offer,
            pkg,
            item.checkIn,
            item.checkOut,
            item.occupancy,
            undefined,
            calendarsByOccupancy,
            availableRatesByOccupancy,
          )
        } else if (pkg) {
          itemTotals = {
            price: pkg.price,
            memberPrice: pkg.memberPrice,
            value: pkg.value,
            taxesAndFees: pkg.taxesAndFees,
            otherFees: {
              propertyFees: pkg.propertyFees,
            },
            memberValue: 0,
            surcharge: 0,
            extraGuestSurcharge: 0,
          }
        }

        const {
          publicTotal,
          memberTotal,
        } = getUserCartItemDisplayTotals(itemTotals)
        let pointsEarnCalculationRequest: App.LoyaltyPointsEarnCalculationRequest | undefined
        if (itemTotals && pkg) {
          pointsEarnCalculationRequest =
            generateLoyaltyPointsCalculatorHotelOptions(offer, {
              pkg,
              price: publicTotal,
              memberPrice: memberTotal,
              currentRegionCode,
            })
        }

        result.push({
          fetching: offerLoading || !pkg || ratesLoading,
          error: !offerLoading && offerError,
          itemId: item.itemId,
          itemType: item.itemType,
          itemTotals,
          luxPlus: {
            hasMemberInclusions: offer.luxPlus.hasMemberInclusions ?? false,
            hasMemberPrices: offer.luxPlus.hasMemberPrices ?? false,
            hasMembershipRequired: !!offer.luxPlus.access,
          },
          isDeleted: itemState?.deleted ?? false,
          isSavedForLater: itemState?.saveForLater ?? false,
          offerId: offer.id,
          sectionHeading: offer.property.name,
          sectionId: offer.property.name,
          groupHeading: pkg?.name,
          roomImage: pkg?.roomType.images[0],
          occupancy: item.occupancy,
          ...isInstantBookingLEHotelItem(item) && {
            startDate: item.checkIn,
            endDate: item.checkOut,
            duration: item.duration,
            subtitle: formatDynamicDateRange(item.checkIn, item.checkOut),
          },
          pointsEarnCalculationRequest: itemsSet.has(item.itemId) ? pointsEarnCalculationRequest : undefined,
          locationHeading: offer.locationSubheading,
          bundledItemIds: item.bundledItemIds,
        })
      }
    })

    bedbankItems.forEach((item) => {
      const offer = cartOffers[item.offerId]
      const offerError = offerErrors[item.offerId]
      const offerLoading = offersLoading[item.offerId]
      const itemState = itemStatesMap.get(item.itemId) ?? defaultItemState
      let itemTotals: App.Checkout.ItemViewTotals | undefined
      let rate: App.BedbankRate | undefined
      let ratesLoading = false

      if (offer && isBedbankOffer(offer)) {
        if (!itemState.deleted) {
          let itemGroup: Array<App.Checkout.BedbankHotelItem>

          if (itemState.saveForLater) {
            itemGroup = [item]
          } else {
            itemGroup = bedbankItems
              .filter(i => {
                const state = itemStatesMap.get(i.itemId)
                return !state?.deleted && !state?.saveForLater
              })
              .filter(i => i.offerId === item.offerId &&
                i.checkIn === item.checkIn &&
                i.checkOut === item.checkOut &&
                matchBedbankRateOrItem(item, i))
          }
          const rateKey = getBedbankRateKey(itemGroup.map(item => item.occupancy), item.checkIn, item.checkOut)

          rate = bedbankOfferRates[item.offerId]?.[rateKey]?.find(r => r.id === item.roomRateId && r.isFlightBundle === item.isFlightBundle)
          if (!rate) {
            rate = bedbankOfferRates[item.offerId]?.[rateKey]?.find(rate => matchBedbankRateOrItem(item, rate))
          }
          ratesLoading = bedbankOfferRatesLoading[item.offerId]?.[rateKey]
        }

        if (rate) {
          itemTotals = getBedbankItemViewTotals(rate, item.occupancy, luxPlusLPPEnabled)
        }
        const {
          publicTotal,
          memberTotal,
        } = getUserCartItemDisplayTotals(itemTotals)
        let pointsEarnCalculationRequest: App.LoyaltyPointsEarnCalculationRequest | undefined
        if (itemTotals) {
          pointsEarnCalculationRequest = generateLoyaltyPointsCalculatorBedbankOptions(rate, {
            price: publicTotal,
            memberPrice: memberTotal,
            propertyFees: itemTotals.propertiesFees?.reduce((acc, fee) => acc + fee.propertyFees, 0) ?? 0,
            offerId: offer.id,
            propertyId: offer.property.id,
            propertyCountryCode: offer.property.address.countryCode,
            currentRegionCode,
          })
        }

        result.push({
          fetching: offerLoading || ratesLoading,
          error: !offerLoading && !ratesLoading && offerError,
          itemId: item.itemId,
          itemType: item.itemType,
          itemTotals,
          isDeleted: itemState?.deleted ?? false,
          isSavedForLater: itemState?.saveForLater ?? false,
          sectionHeading: offer.property.name,
          sectionId: offer.property.name,
          subtitle: item.checkIn && item.checkOut ?
              formatDynamicDateRange(item.checkIn, item.checkOut) :
            undefined,
          startDate: item.checkIn,
          endDate: item.checkOut,
          offerId: item.offerId,
          duration: item.duration,
          groupHeading: rate?.room?.name,
          occupancy: item.occupancy,
          roomImage: offer.images?.[0],
          pointsEarnCalculationRequest: itemsSet.has(item.itemId) ? pointsEarnCalculationRequest : undefined,
          locationHeading: offer.property.locationTitleOverride ?? offer.property.address.countryName,
          paymentMethod: rate?.paymentMethod,
          bundledItemIds: item.bundledItemIds,
        })
      }
    })

    flightItems.forEach((item) => {
      const itemState = itemStatesMap.get(item.itemId) ?? defaultItemState
      const firstFlight = item.flights[0]
      const lastFlight = last(item.flights)
      const startDate = firstFlight.departureDateTime
      const endDate = last(item.flights).arrivalDateTime
      const fromCity = firstFlight.departingAirportName
      const toCity = item.viewType === FlightViewTypes.MULTI_CITY ? lastFlight.arrivalAirportName : firstFlight.arrivalAirportName

      const airportsByCode = arrayToMap(airports, airport => airport.code)

      const loyaltyProductTypes = item.flights.map(flight => {
        const departingAirport = airportsByCode.get(flight.departingAirportCode)
        const arrivalAirport = airportsByCode.get(flight.arrivalAirportCode)

        const isDomestic = departingAirport?.countryCode === arrivalAirport?.countryCode
        return getFlightLoyaltyProductType(flight.fareFamily?.cabin?.toLowerCase() ?? 'economy', isDomestic)
      })

      const rankedLoyaltyProductTypes = sortBy(loyaltyProductTypes, productType => getFlightLoyaltyProductTypeRank(productType), 'asc')
      const highestRankedProductType = rankedLoyaltyProductTypes[0]

      const flightTotal = getFlightsTotal(item)

      const earnKey = getLoyaltyEarnKeyFromFlightItems(item.flights, highestRankedProductType, airports)

      const pointsEarnCalculationRequest: App.LoyaltyPointsEarnCalculationRequest = {
        loyaltyProductType: highestRankedProductType,
        price: flightTotal,
        earnKey,
        offerId: '',
        packageId: '',
      }
      result.push({
        fetching: false,
        error: false,
        occupancy: item.occupants,
        itemId: item.itemId,
        startDate,
        endDate,
        sectionHeading: TITLE_MAPPER[item.fareType],
        sectionId: item.itemId,
        subtitle: `${fromCity} to ${toCity}`,
        itemTotals: {
          price: flightTotal,
          memberPrice: 0,
          value: flightTotal,
          memberValue: 0,
          surcharge: 0,
          extraGuestSurcharge: 0,
          taxesAndFees: 0,
        },
        itemType: item.itemType,
        isDeleted: itemState?.deleted ?? false,
        isSavedForLater: itemState?.saveForLater ?? false,
        pointsEarnCalculationRequest: itemsSet.has(item.itemId) ? pointsEarnCalculationRequest : undefined,
        locationHeading: toCity,
        bundledItemIds: item.bundledItemIds,
      })
    })

    experienceItems.forEach((item) => {
      const offer = cartOffers?.[item.experienceId]
      const offerError = experiencesError[item.experienceId]
      const offerLoading = experiencesLoading[item.experienceId]
      const itemState = itemStatesMap.get(item.itemId) ?? defaultItemState
      const experienceTimeSlots = experienceTimesMap[item.itemId]

      if (isExperienceOffer(offer)) {
        const timeSlots = experienceTimeSlots?.slots ?? []
        const tickets: CartItemData['tickets'] = []
        const hasTickets = timeSlots.some(slot => slot.tickets.length)
        let itemTotals: App.Checkout.ItemViewTotals | undefined

        if (hasTickets && timeSlots) {
          const timeSlotTickets = timeSlots.flatMap(slot =>
            slot.tickets.map(ticket => ({ ...ticket, time: slot.time })),
          )
          const requiresTime = offer.bookingType === 'CALENDAR-TIMESLOTS' && !item.isBuyNowBookLater && !item.isGift

          const ticketViews = item.tickets
            .filter(ticket => ticket.count > 0)
            .map(ticketItem => {
              const ticket = timeSlotTickets.find(t =>
                t.ticketId === ticketItem.ticketId &&
                t.date === ticketItem.date &&
                (!requiresTime || t.time === item.time),
              )
              if (!ticket) return null

              return {
                name: ticket.name,
                count: ticket.count,
                price: ticket.price,
                memberPrice: ticket.memberPrice,
                value: ticket.value,
                taxesAndFees: ticket.taxesAndFees,
                discounts: ticket.discounts,
              }
            })
            .filter(isNonNullable)

          tickets.push(...ticketViews.map(view => ({
            name: view.name,
            count: view.count,
            price: view.price,
          })))
          if (ticketViews.length > 0) {
            const price = sum(ticketViews, tv => tv.price)
            const memberPrice = sum(ticketViews, tv => tv.memberPrice ?? 0)
            const value = sum(ticketViews, tv => tv.value)
            const taxesAndFees = sum(ticketViews, tv => tv.taxesAndFees ?? 0)
            const appPriceTickets = ticketViews.filter(tv => tv.discounts.app.amount > 0)
            const appDiscountAmount = sum(appPriceTickets, tv => tv.discounts.app.amount ?? 0)
            const appDiscountPercentage = appDiscountAmount > 0 ? sum(appPriceTickets, tv => tv.discounts.app.percentage ?? 0) / appPriceTickets.length : 0

            itemTotals = {
              price,
              memberPrice,
              value,
              mobileAppDiscount: {
                amount: appDiscountAmount,
                percentage: appDiscountPercentage,
              },
              taxesAndFees,
              surcharge: 0,
              memberValue: 0,
              extraGuestSurcharge: 0,
            }
          }
        }
        let pointsEarnCalculationRequest: App.LoyaltyPointsEarnCalculationRequest | undefined
        const {
          publicTotal,
        } = getUserCartItemDisplayTotals(itemTotals)
        if (itemTotals) {
          pointsEarnCalculationRequest = {
            price: publicTotal,
            loyaltyProductType: 'experience',
          }
        }

        result.push({
          fetching: offerLoading,
          error: !offerLoading && offerError,
          itemId: item.itemId,
          itemType: item.itemType,
          itemTotals,
          isDeleted: itemState?.deleted ?? false,
          isSavedForLater: itemState?.saveForLater ?? false,
          sectionHeading: offer.name,
          sectionId: offer.name,
          offerId: offer.id,
          subtitle: formatExperienceDateRange(item, offer),
          tickets,
          startDate: offer.hasCalendar ? item.date : undefined,
          roomImage: offer.images?.[0],
          pointsEarnCalculationRequest: itemsSet.has(item.itemId) ? pointsEarnCalculationRequest : undefined,
          locationHeading: offer.location.name,
        })
      }
    })

    transferItems.forEach((item) => {
      const offer = cartOffers?.[item.experienceId]
      const offerError = experiencesError[item.experienceId]
      const offerLoading = experiencesLoading[item.experienceId]
      const itemState = itemStatesMap.get(item.itemId) ?? defaultItemState

      if (offer && isExperienceOffer(offer)) {
        const formattedDateRange = item.transfer.date ?
          item.transfer.time ?
            `${moment(item.transfer.date).format(ACCOMMODATION_TIME_FORMAT)} · ${item.transfer.time}` :
              moment(item.transfer.date).format(ACCOMMODATION_TIME_FORMAT) :
          undefined

        const itemTotals: App.Checkout.ItemViewTotals = {
          price: item.transfer.option?.price ?? 0,
          memberPrice: 0,
          value: item.transfer.option?.price ?? 0,
          mobileAppDiscount: {
            amount: item.transfer.option?.discounts?.app.amount ?? 0,
            percentage: item.transfer.option?.discounts?.app.percentage ?? 0,
          },
          taxesAndFees: item.transfer.option?.taxesAndFees ?? 0,
          surcharge: 0,
          memberValue: 0,
          extraGuestSurcharge: 0,
        }

        const pointsEarnCalculationRequest: App.LoyaltyPointsEarnCalculationRequest | undefined = generateLoyaltyPointsCalculatorExperienceOptions(offer, {})

        result.push({
          fetching: offerLoading,
          error: !offerLoading && offerError,
          itemId: item.itemId,
          itemType: item.itemType,
          itemTotals,
          isDeleted: itemState?.deleted ?? false,
          isSavedForLater: itemState?.saveForLater ?? false,
          sectionHeading: offer.name,
          sectionId: offer.name,
          offerId: offer.id,
          subtitle: formattedDateRange,
          groupHeading: item.transfer.option?.name,
          roomImage: offer.images?.[0],
          maxPassengers: item.transfer.option?.maxPassengers,
          maxLuggage: item.transfer.option?.maxCheckedBags,
          occupancy: {
            adults: item.transfer.travellers.adults,
            children: item.transfer.travellers.children,
          },
          startDate: item.transfer.date,
          endDate: item.transfer.date,
          pointsEarnCalculationRequest: itemsSet.has(item.itemId) ? pointsEarnCalculationRequest : undefined,
        })
      }
    })

    if (subscriptionItem) {
      const offer = subscriptionOffers?.find(o => o.joinFeeWaived === waiveLuxPlusJoiningFee)
      const itemState = itemStatesMap.get(subscriptionItem.itemId) ?? defaultItemState

      result.push({
        fetching: subscriptionOffersLoading,
        error: subscriptionOffersError,
        itemId: subscriptionItem.itemId,
        itemType: subscriptionItem.itemType,
        itemTotals: offer ? {
          price: offer.price,
          memberPrice: offer.price,
          value: offer.price,
          taxesAndFees: 0,
          surcharge: 0,
          memberValue: 0,
          extraGuestSurcharge: 0,
        } : undefined,
        sectionId: FIXED_SECTION_NAMES.LUXPLUS,
        isDeleted: itemState?.deleted ?? false,
        isSavedForLater: itemState?.saveForLater ?? false,
      }, {
        fetching: subscriptionOffersLoading,
        error: subscriptionOffersError,
        itemId: subscriptionItem.itemId,
        itemType: CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE,
        itemTotals: offer ? {
          price: waiveLuxPlusJoiningFee ? 0 : offer.joinFee,
          memberPrice: waiveLuxPlusJoiningFee ? 0 : offer.joinFee,
          value: offer.joinFee,
          taxesAndFees: 0,
          surcharge: 0,
          memberValue: 0,
          extraGuestSurcharge: 0,
        } : undefined,
        sectionId: FIXED_SECTION_NAMES.LUXPLUS,
        isDeleted: itemState?.deleted ?? false,
        isSavedForLater: itemState?.saveForLater ?? false,
      })
    }

    insuranceItems.forEach((item) => {
      const itemState = itemStatesMap.get(item.itemId) ?? defaultItemState

      result.push({
        error: false,
        fetching: false,
        itemId: item.itemId,
        itemType: item.itemType,
        sectionId: FIXED_SECTION_NAMES.INSURANCE,
        isDeleted: itemState?.deleted ?? false,
        isSavedForLater: itemState?.saveForLater ?? false,

      })
    })

    merchandiseItems.forEach((item) => {
      const itemState = itemStatesMap.get(item.itemId) ?? defaultItemState
      const offer = merchandiseOffers.find(o => o.id === item.advertId)
      const variant = offer?.variants.find(v => v.id === item.variantId)

      const hasAttemptedFetch = !merchandiseFetching[item.advertId]
      const hasNoData = !offer
      const hasError = !!merchandiseErrors[item.advertId]
      const isMissing = hasAttemptedFetch && hasNoData && !hasError

      const pointsEarnCalculationRequest: App.LoyaltyPointsEarnCalculationRequest | undefined = generateLoyaltyPointsCalculatorMerchandiseOptions(variant, {
        price: variant?.price ?? 0,
      })

      result.push({
        fetching: !offer && !hasAttemptedFetch,
        error: isMissing || hasError,
        itemId: item.itemId,
        itemType: item.itemType,
        pointsEarnCalculationRequest: itemsSet.has(item.itemId) ? pointsEarnCalculationRequest : undefined,
        isDeleted: itemState?.deleted ?? false,
        isSavedForLater: itemState?.saveForLater ?? false,
        sectionId: variant?.id ?? item.itemId,
        sectionHeading: offer?.name ?? '',
        subtitle: variant ? getMerchandiseVariantOptionText(variant) : undefined,
        itemTotals: {
          price: variant?.price ?? 0,
          memberPrice: 0,
          value: 0,
          memberValue: 0,
          surcharge: 0,
          extraGuestSurcharge: 0,
          taxesAndFees: 0,
        },
      })
    })

    return sortBy(result, item => itemOrderMap.get(item.itemType) ?? 0, 'asc')
  }, [
    items,
    itemsSet,
    cartOffers,
    calendarsByOccupancyLoading,
    calendarsByOccupancy,
    availableRatesByOccupancy,
    subscriptionOffers,
    subscriptionOffersLoading,
    subscriptionOffersError,
    itemStatesMap,
    offerErrors,
    offersLoading,
    experiencesError,
    experiencesLoading,
    bedbankOfferRates,
    bedbankOfferRatesLoading,
    luxPlusLPPEnabled,
    experienceTimesMap,
    bedbankItems,
    experienceItems,
    flightItems,
    waiveLuxPlusJoiningFee,
    airports,
    merchandiseItems,
    merchandiseOffers,
    merchandiseFetching,
    merchandiseErrors,
    currentRegionCode,
  ])

  return {
    error: metadataItems.some(item => item.error),
    fetching: metadataItems.some(item => item.fetching),
    items: metadataItems,
  }
}

export default useAllUserCartItemsMetadata
